import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Autocomplete, Box, Grid, TextField, Typography } from '@mui/material'
import * as React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SEARCH_POSTAL_CODES_QUERY } from '../../../graphql/queries/postalCodes/searchPostalCodesQuery'
import { useDebouncedQuery } from '../../../utils/hooks/useDebouncedQuery'
import { SearchPostalCodesQuery } from '../../../graphql/queries/postalCodes/__generated__/searchPostalCodesQuery.generated'

type PostalCode = SearchPostalCodesQuery['searchPostalCodes']['results'][number]

interface AddressAutocompleteProps {
  disabled?: boolean
  label?: string
  name?: string
  onChange?: (value: any) => void
  onSelected?: (value: any) => void
  options?: any
  value?: string
  error?: boolean
  helperText?: string
}

const AddressAutocomplete = React.forwardRef(
  (
    {
      disabled,
      label,
      name,
      onChange,
      onSelected,
      options,
      value,
      error,
      helperText
    }: AddressAutocompleteProps,
    ref
  ): React.JSX.Element => {
    const { t } = useTranslation('common')
    const methods = useFormContext()
    const { setValue } = methods || {}

    const [inputValue, setInputValue] = React.useState(value || '')
    React.useEffect(() => {
      if (value !== inputValue) setInputValue(value || '')
    }, [value, setInputValue, inputValue])

    const {
      results: postalResults,
      loading,
      debouncedSearch
    } = useDebouncedQuery<
      { searchPostalCodes: SearchPostalCodesQuery['searchPostalCodes'] },
      PostalCode,
      { query: string; country?: string; limit?: number }
    >({
      query: SEARCH_POSTAL_CODES_QUERY,
      transformData: data => data.searchPostalCodes?.results ?? ([] as PostalCode[]),
      minChars: 1,
      debounceTime: 400,
      buildSearchVariables: searchText => ({
        query: searchText.trim(),
        limit: 10
      })
    })

    React.useEffect(() => {
      if (inputValue.trim()) {
        debouncedSearch(inputValue)
      }
    }, [inputValue, debouncedSearch])

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value)
      onChange?.(event.target.value)
    }

    const handleSelectionChange = (
      _event: React.ChangeEvent<{}>,
      val: string | PostalCode | null
    ) => {
      if (typeof val === 'string') return
      if (!val) return

      const postalCode = val.zipcode
      const place = val.place

      onChange?.(val)
      setValue(name || '', postalCode, { shouldDirty: true, shouldValidate: true })
      setValue(options?.dependency || '', place, { shouldDirty: true, shouldValidate: true })
      onSelected?.(val)
    }

    return (
      <Autocomplete<PostalCode, false, false, true>
        disabled={disabled}
        filterOptions={x => x}
        size="small"
        freeSolo
        loading={loading}
        options={postalResults}
        getOptionLabel={option => (typeof option === 'string' ? option : option.zipcode || '')}
        isOptionEqualToValue={(option, val) =>
          typeof val === 'string' ? option.zipcode === val : option.zipcode === val.zipcode
        }
        inputValue={inputValue}
        onInputChange={(_, newInputValue) =>
          handleInputChange({
            target: { value: newInputValue }
          } as React.ChangeEvent<HTMLInputElement>)
        }
        noOptionsText={t('noOptions')}
        onChange={handleSelectionChange}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props
          const extendedKey = `${key}-${option.zipcode}-${option.place}`

          return (
            <li key={extendedKey} {...optionProps}>
              <Grid container sx={{ alignItems: 'center' }}>
                <Grid sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>
                    {option.zipcode}
                  </Box>
                  {option.place ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {option.place}
                      {option.state ? `, ${option.state}` : ''}
                      {option.countryCode ? ` (${option.countryCode})` : ''}
                    </Typography>
                  ) : null}
                </Grid>
              </Grid>
            </li>
          )
        }}
        renderInput={params => (
          <TextField
            {...params}
            error={error}
            helperText={helperText}
            label={label}
            variant="outlined"
            inputRef={ref}
          />
        )}
      />
    )
  }
)

export default AddressAutocomplete
